import React from "react";
import i18next from "src/i18n/config";
import semverService from "src/services/semverService/semverService";

import { BuildEvents, EventReport } from "@dashboard/devices/types";
import { Product } from "@products/types";
import { TextLink } from "@shared/TextLink";

import type { EventContext } from "./eventTypes";

const productRoute = (product: Product) => `/dashboard/products/${product.codename}/${product.androidVersion}`;

/**
 * Event text is rendered through `<Trans>`, which parses the interpolated value as markup before inserting it.
 * Event data is generated by the backend and is not expected to contain any, so this is a safety net rather than
 * a fix: dropping the two characters that could turn a name into a tag keeps that guarantee here, next to the
 * rendering, instead of relying on it holding everywhere upstream.
 */
export const extractPlainText = (value: string | number | undefined | null): string => String(value ?? "").replace(/[<>]/g, "");

// Android reports `sys.boot.reason` as `<reason>,<subreason>`, e.g. `reboot,userrequested`. The leading
// token carries nothing a user acts on, so only the remainder is shown.
export const formatRebootReason = (reason: string | null | undefined): string => {
    const value = extractPlainText(reason);
    const separatorIndex = value.indexOf(",");
    return (separatorIndex === -1 ? value : value.slice(separatorIndex + 1)).trim();
};

export const getActorTitle = (event: EventReport, fallback?: string): string | undefined => {
    const title = event.actorTitle && event.actorTypeName !== "Anonymous" ? event.actorTitle : fallback;
    return title === undefined ? undefined : extractPlainText(title);
};

export const createLicenseLink = (event: EventReport, context: EventContext): React.ReactElement => {
    const { license, handleLicenseClick } = context;
    if (license && handleLicenseClick) {
        return (
            <TextLink
                onClick={() => handleLicenseClick(event)}
                tooltip={i18next.t("modals:eventModals.tooltip.showThisLicense")}
            />
        );
    }
    return <span />;
};

export const createDeviceLink = (event: EventReport, context: EventContext): React.ReactElement => {
    const { device, handleDeviceNameClick } = context;
    if (device && handleDeviceNameClick) {
        return (
            <TextLink
                onClick={() => handleDeviceNameClick(event)}
                tooltip={i18next.t("modals:eventModals.tooltip.showThisDevice")}
            />
        );
    }
    return <span />;
};

export const createGroupLink = (event: EventReport, context: EventContext): React.ReactElement => {
    const { group, handleGroupNameClick } = context;
    if (group && handleGroupNameClick) {
        return (
            <TextLink
                onClick={() => handleGroupNameClick(event)}
                tooltip={i18next.t("modals:eventModals.tooltip.showThisGroup")}
            />
        );
    }
    return <span />;
};

export const createProductBuildLink = (event: BuildEvents, context: EventContext): React.ReactElement => {
    const { navigate, products, builds } = context;

    const majorAndroidVersion = semverService.getMajorVersion(event.data?.version)?.toString();
    const product = products?.find((p) => p.codename === event.data?.codename && p.androidVersion === majorAndroidVersion);
    const buildExists = builds?.some((b) => b.productId === product?.id && b.version === event.data?.version);

    if (navigate && product && buildExists) {
        return (
            <TextLink
                tooltip={i18next.t("modals:eventModals.tooltip.showThisProduct")}
                onClick={() => navigate(productRoute(product))}
            />
        );
    }
    return <span />;
};

export const findEventProduct = (event: EventReport, context: EventContext): Product | undefined =>
    context.products?.find((product) => product.id === Number(event.entityId));

export const createProductLink = (product: Product, context: EventContext): React.ReactElement => {
    const { navigate } = context;
    if (navigate) {
        return (
            <TextLink
                tooltip={i18next.t("modals:eventModals.tooltip.showThisProduct")}
                onClick={() => navigate(productRoute(product))}
            />
        );
    }
    return <span />;
};
